import {action, computed, observable} from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    inject,
    Popout, PopoutHandle,
} from 'Mri.Web.Common';
import * as React from 'react';

import {
    Button,
    Input, InputGroup,
} from 'reactstrap';
import {AccountStore, IUserGridColumnPreset} from '../../Account/AccountStore';
import { AppStore } from '../../Core/AppStore';
import {CellStyle, ColDef, Column} from "ag-grid-enterprise";
import {DisplayListViewModel} from "../../DisplayFilters/DisplayListViewModel";

export interface IGridSettingsProps {
    readonly popoutHandle: PopoutHandle;
    readonly vm: DisplayListViewModel;
    readonly className?: string;
}

@autoinject
@observer
export class GridSettings extends React.Component<IGridSettingsProps> {
    @bind
    @action
    componentDidMount() {
        this.setDefaults();
        this.columnWidth = this.accountStore.storage.gridColumnWidth;
        this.columnPadding = this.accountStore.storage.gridColumnPadding;
        this.groupHeaderHeight = this.accountStore.storage.gridGroupHeaderHeight;
        this.rowHeight = this.accountStore.storage.gridRowHeight;
        this.popVuRowHeight = this.accountStore.storage.gridPopVuRowHeight;
        this.leftPinnedContainerWidth = this.accountStore.storage.gridLeftPinnedContainerWidth || 30;
        this.rightPinnedContainerWidth = this.accountStore.storage.gridRightPinnedContainerWidth || 30;
        this.gridDecimals = this.accountStore.storage.gridDecimals;
    }

    public render() {
        const { popoutHandle } = this.props;

        return (
            <Popout popoutHandle={popoutHandle} size="side">
                <div className="py-0 px-2">
                    <h3>Grid Settings</h3>

                    <div className="mb-2">
                        <label>Wrap Text</label>
                        <Input type="switch" role="switch" className="ms-2"
                               defaultChecked={this.accountStore.storage.gridWrapText}
                               onClick={() => this.onToggleWrapText()}/>
                    </div>

                    <div className="mb-2">
                        <label>Bold Text</label>
                        <Input type="switch" role="switch" className="ms-2"
                               defaultChecked={this.accountStore.storage.gridBoldText}
                               onClick={() => this.onToggleBoldText()}/>
                    </div>

                    <div className="mb-2">
                        <label>Hide Units</label>
                        <Input type="switch" role="switch" className="ms-2"
                               defaultChecked={this.accountStore.storage.hideUnits}
                               onClick={() => this.onToggleHideUnits()}/>
                    </div>

                    <div className="mb-2">
                        <label>Center Align Header</label>
                        <Input type="switch" role="switch" className="ms-2"
                               defaultChecked={this.accountStore.storage.gridCenterAlignHeaders}
                               onClick={() => this.onToggleGridCenterAlignHeaders()}/>
                    </div>

                    <div className="mb-2">
                        <label>Center Align Columns</label>
                        <Input type="switch" role="switch" className="ms-2"
                               defaultChecked={this.accountStore.storage.gridCenterAlignColumns}
                               onClick={() => this.onToggleGridCenterAlignColumns()}/>
                    </div>

                    <div className="mb-2">
                        <label>Left Pins Scrollable</label>
                        <Input type="switch" role="switch" className="ms-2"
                               defaultChecked={this.accountStore.storage.gridLeftPinnedContainerScrollable}
                               onClick={() => this.onToggleGridLeftPinnedContainerScrollable()}/>
                    </div>

                    <div className="mb-2">
                        <label>Right Pins Scrollable</label>
                        <Input type="switch" role="switch" className="ms-2"
                               defaultChecked={this.accountStore.storage.gridRightPinnedContainerScrollable}
                               onClick={() => this.onToggleGridRightPinnedContainerScrollable()}/>
                    </div>

                    <div className="mb-2">
                        <label>
                            Font Size
                        </label>
                        <Input
                            type="select"
                            name="fontSize"
                            id="gridSizeSelect"
                            value={this.accountStore.storage.gridThemeSize}
                            onChange={this.onGridThemeSizeChange}
                        >
                            <option value="grid-xxsmall">Extra Extra Small</option>
                            <option value="grid-xsmall">Extra Small</option>
                            <option value="grid-small">Small</option>
                            <option value="grid-normal">Normal</option>
                            <option value="grid-large">Large</option>
                        </Input>
                    </div>

                    <div className="mb-2">
                        <label>
                            Value Decimals
                        </label>
                        <InputGroup>
                            <Input defaultValue={this.accountStore.storage.gridDecimals || this.gridDecimals}
                                   onChange={this.onGridDecimalsChange}/>
                            <Button onClick={() => this.changeDecimals()}>Apply</Button>
                        </InputGroup>
                    </div>

                    <div className="mb-2">
                        <label>
                            Theme
                        </label>
                        <Input
                            type="select"
                            name="gridTheme"
                            id="gridThemeSelect"
                            value={this.accountStore.storage.gridTheme}
                            onChange={this.onGridThemeChange}
                        >
                            <option value="ag-theme-balham">Light</option>
                            <option value="ag-theme-balham-dark">Dark</option>
                        </Input>
                    </div>

                    <div className="mb-2">
                        <label>
                            Column Width (Pixels)
                        </label>
                        <InputGroup>
                            <Input placeholder="Recommend between 30-200"
                                   defaultValue={this.accountStore.storage.gridColumnWidth}
                                   onChange={this.onColumnWidthChange}/>
                            <Button onClick={() => this.resizeColumns()}>Apply</Button>
                        </InputGroup>
                    </div>

                    <div className="mb-2">
                        <label>
                            Column Padding (Pixels)
                        </label>
                        <InputGroup>
                            <Input placeholder="Recommend 0-3"
                                   defaultValue={this.accountStore.storage.gridColumnPadding}
                                   onChange={this.onColumnPaddingChange}/>
                            <Button onClick={() => this.applyPadding()}>Apply</Button>
                        </InputGroup>
                    </div>

                    <div className="mb-2">
                        <label>
                            Group Header Height (Pixels) - Set to 0 to hide
                        </label>
                        <InputGroup>
                            <Input placeholder="Recommend between 15-40"
                                   defaultValue={this.accountStore.storage.gridGroupHeaderHeight}
                                   onChange={this.onGroupHeaderHeightChange}/>
                            <Button onClick={() => this.resizeGroupHeader()}>Apply</Button>
                        </InputGroup>
                    </div>

                    <div className="mb-2">
                        <label>
                            Row Height Without PopVu (Pixels) - Recommended 30-100
                        </label>
                        <InputGroup>
                            <Input placeholder="Recommended between 30-100"
                                   defaultValue={this.accountStore.storage.gridRowHeight}
                                   onChange={this.onRowHeightChange}/>
                            <Button onClick={() => this.resizeRows()}>Apply</Button>
                        </InputGroup>
                    </div>

                    <div className="mb-2">
                        <label>
                            Row Height With PopVu (Pixels) - Recommended 200
                        </label>
                        <InputGroup>
                            <Input placeholder="Recommend 200"
                                   defaultValue={this.accountStore.storage.gridPopVuRowHeight || this.popVuRowHeight}
                                   onChange={this.onPopVuRowHeightChange}/>
                            <Button onClick={() => this.resizePopVuRows()}>Apply</Button>
                        </InputGroup>
                    </div>
                </div>
            </Popout>
        );
    }

    @bind
    @action
    private onRowHeightChange(event: React.ChangeEvent<HTMLInputElement>) {
        this.rowHeight = parseInt(event.target.value);
    }

    @bind
    @action
    private onPopVuRowHeightChange(event: React.ChangeEvent<HTMLInputElement>) {
        this.popVuRowHeight = parseInt(event.target.value);
    }

    @bind
    @action
    private onGroupHeaderHeightChange(event: React.ChangeEvent<HTMLInputElement>) {
        this.groupHeaderHeight = parseInt(event.target.value);
    }

    @bind
    @action
    private onColumnWidthChange(event: React.ChangeEvent<HTMLInputElement>) {
        this.columnWidth = parseInt(event.target.value);
    }

    @bind
    @action
    private onGridDecimalsChange(event: React.ChangeEvent<HTMLInputElement>) {
        this.gridDecimals = parseInt(event.target.value);
    }

    @bind
    @action
    private onLeftPinnedContainerWidthChange(event: React.ChangeEvent<HTMLInputElement>) {
        const value = parseInt(event.target.value, 10);
        this.leftPinnedContainerWidth = value / 100;
    }


    @bind
    @action
    private onRightPinnedContainerWidthChange(event: React.ChangeEvent<HTMLInputElement>) {
        const value = parseInt(event.target.value, 10);
        this.rightPinnedContainerWidth = value / 100;
    }

    @bind
    @action
    private onColumnPaddingChange(event: React.ChangeEvent<HTMLInputElement>) {
        this.columnPadding = parseInt(event.target.value);
    }
    
    @bind
    @action
    private resizeGroupHeader() {
        this.accountStore.storage.gridGroupHeaderHeight = this.groupHeaderHeight;

        this.appStore.gridRef?.api.setGridOption('groupHeaderHeight', this.groupHeaderHeight);

        let currentColumns = this.appStore.gridRef?.api.getColumnState();

        this.accountStore.tabStorage.activeGridColumns = currentColumns || [];
    }

    @bind
    @action
    private resizeRows() {
        this.accountStore.storage.gridRowHeight = this.rowHeight;

        if(!this.isImageColumnSelected) {
            this.appStore.gridRef?.api.setGridOption('rowHeight', this.rowHeight);
            let currentColumns = this.appStore.gridRef?.api.getColumnState();

            this.accountStore.tabStorage.activeGridColumns = currentColumns || [];
        }
    }

    @bind
    @action
    private resizePopVuRows() {
        this.accountStore.storage.gridPopVuRowHeight = this.popVuRowHeight;
        if(this.isImageColumnSelected) {
            this.appStore.gridRef?.api.setGridOption('rowHeight', this.popVuRowHeight);
            let currentColumns = this.appStore.gridRef?.api.getColumnState();

            this.accountStore.tabStorage.activeGridColumns = currentColumns || [];
        }
    }

    @bind
    @action
    private changeDecimals() {
        this.accountStore.storage.gridDecimals = this.gridDecimals;
    }
    
    @bind
    @action
    private resizeColumns() {
        this.accountStore.storage.gridColumnWidth = this.columnWidth;
        
        let columns = this.appStore.gridRef?.api.getColumns()
        let newWidths: {key: string | ColDef | Column, newWidth: number}[] = [];

        if(columns) newWidths = columns.map((column: Column) => {
            return {key: column, newWidth: this.columnWidth}
        });
        this.appStore.gridRef?.api.setColumnWidths(newWidths);
        
        let currentColumns = this.appStore.gridRef?.api.getColumnState();

        this.accountStore.tabStorage.activeGridColumns = currentColumns || [];
    }

    @bind
    @action
    private applyPadding() {
        this.accountStore.storage.gridColumnPadding = this.columnPadding;

        let columns = this.appStore.gridRef?.api.getColumns()
        // let styles: {key: string | ColDef | Column, cellStyle: CellStyle}[] = [];
        //
        // if(columns) styles = columns.map((column: Column) => {
        //     return {key: column, cellStyle: this.columnWidth}
        // });
        //this.appStore.gridRef?.api.setcolumn(newWidths);

        let currentColumns = this.appStore.gridRef?.api.getColumnState();

        this.accountStore.tabStorage.activeGridColumns = currentColumns || [];
    }

    @bind
    @action
    private setLeftPinnedContainerWidth() {
        this.accountStore.storage.gridLeftPinnedContainerWidth = this.leftPinnedContainerWidth;

        this.refreshGrid();
    }

    @bind
    @action
    private setRightPinnedContainerWidth() {
        this.accountStore.storage.gridRightPinnedContainerWidth = this.rightPinnedContainerWidth;
        this.refreshGrid();
    }

    @bind
    @action
    private onGridThemeSizeChange(event: React.ChangeEvent<HTMLInputElement>) {
        this.accountStore.storage.gridThemeSize = event.target.value as any;
    }
    
    @bind
    @action
    private onToggleWrapText() {
        this.accountStore.storage.gridWrapText = !this.accountStore.storage.gridWrapText;

        this.refreshGrid();
    }

    @bind
    @action
    private onToggleBoldText() {
        this.accountStore.storage.gridBoldText = !this.accountStore.storage.gridBoldText;
    }

    @bind
    @action
    private onToggleHideUnits() {
        this.accountStore.storage.hideUnits = !this.accountStore.storage.hideUnits;
    }

    @bind
    @action
    private onGridThemeChange(event: React.ChangeEvent<HTMLInputElement>) {
        this.accountStore.storage.gridTheme = event.target.value as any;
    }

    @bind
    @action
    private onToggleGridCenterAlignHeaders() {
        this.accountStore.storage.gridCenterAlignHeaders = !this.accountStore.storage.gridCenterAlignHeaders;
    }

    @bind
    @action
    private onToggleGridCenterAlignColumns() {
        this.accountStore.storage.gridCenterAlignColumns = !this.accountStore.storage.gridCenterAlignColumns;
    }

    @bind
    @action
    private onToggleGridLeftPinnedContainerScrollable() {
        this.accountStore.storage.gridLeftPinnedContainerScrollable = !this.accountStore.storage.gridLeftPinnedContainerScrollable;

        this.refreshGrid();
    }

    @bind
    @action
    private onToggleGridRightPinnedContainerScrollable() {
        this.accountStore.storage.gridRightPinnedContainerScrollable = !this.accountStore.storage.gridRightPinnedContainerScrollable;

        this.refreshGrid();
    }

    @bind
    @action
    private refreshGrid() {
        this.appStore.gridInitialized = false;
        this.appStore.gridKey = 0;
    }
    
    @bind
    @action
    private setDefaults(){
        let storage = this.accountStore.storage;
        if(!storage.gridLeftPinnedContainerWidth) storage.gridLeftPinnedContainerWidth = .30;
        if(!storage.gridRightPinnedContainerWidth) storage.gridRightPinnedContainerWidth = .30;
        if(!storage.gridPopVuRowHeight) storage.gridPopVuRowHeight = 200;
        if(!storage.gridDecimals) storage.gridDecimals = 2;
    }

    @computed
    private get isImageColumnSelected() {
        let isPopVu1Selected = this.appStore.gridRef?.api.getColumn("latestSnapshot1");
        let isPopVu2Selected = this.appStore.gridRef?.api.getColumn("latestSnapshot2");
        let isPopVu3Selected = this.appStore.gridRef?.api.getColumn("latestSnapshot3");
        let isPopVu4Selected = this.appStore.gridRef?.api.getColumn("latestSnapshot4");
        let isSelected = false;
        if (isPopVu1Selected && isPopVu1Selected.isVisible()) isSelected = true;
        if (isPopVu2Selected && isPopVu2Selected.isVisible()) isSelected = true;
        if (isPopVu3Selected && isPopVu3Selected.isVisible()) isSelected = true;
        if (isPopVu4Selected && isPopVu4Selected.isVisible()) isSelected = true;

        return isSelected;
    }

    @observable
    private columnWidth: number = 0;
    @observable
    private gridDecimals: number = 2;
    @observable
    private leftPinnedContainerWidth: number = 30;
    @observable
    private rightPinnedContainerWidth: number = 30;
    @observable
    private columnPadding: number = 2;
    @observable
    private groupHeaderHeight: number = 0;
    @observable
    private rowHeight: number = 40;
    @observable
    private popVuRowHeight: number = 200;
    @inject
    private accountStore!: AccountStore;
    @inject
    private appStore!: AppStore;
}
